import type { Placement, Rect } from '@popperjs/core';

type CalculateOffsetOptions = {
  popper: Rect;
  placement: Placement;
  rootElement: HTMLDivElement;
  referenceElement: HTMLDivElement;
  boundaryElement: HTMLElement;
};

type CalculateHorizontalOffsetOptions = {
  popper: Rect;
  rootElement: HTMLDivElement;
  boundaryElement: HTMLElement;
  referenceElement: HTMLDivElement;
};

type CalculateMenuOverflowOptions = {
  popperWidth: number;
  referencePosition: ReturnType<typeof getReferencePosition>;
  boundaryWidth: number;
  referenceWidth: number;
  isRTL: boolean;
};

const HORIZONTAL_PADDING_OFFSET = 14;

const getStylePxAsNumber = (value: string) => {
  return parseFloat(value.replace('px', ''));
};

const calculateVerticalOffset = (
  rootElement: HTMLDivElement,
  placement: Placement,
) => {
  const { borderTopWidth, borderBottomWidth } = getComputedStyle(rootElement);

  if (placement.startsWith('top')) {
    return getStylePxAsNumber(borderTopWidth);
  }

  return getStylePxAsNumber(borderBottomWidth);
};

const getElementDimensions = (element: HTMLElement) => {
  const rect = element.getBoundingClientRect();

  return {
    width: rect.width,
    left: rect.left,
  };
};

const getIsRTL = (rootElement: HTMLDivElement) => {
  const { direction } = getComputedStyle(rootElement);
  return direction === 'rtl';
};

const getReferencePosition = ({
  boundaryDimensions,
  referenceDimensions,
}: {
  boundaryDimensions: ReturnType<typeof getElementDimensions>;
  referenceDimensions: ReturnType<typeof getElementDimensions>;
}) => {
  const offsetFromBoundary = referenceDimensions.left - boundaryDimensions.left;
  const endPosition = offsetFromBoundary + referenceDimensions.width;
  const overflowRight = boundaryDimensions.width - endPosition;
  const overflowLeft = referenceDimensions.left - boundaryDimensions.left;

  return {
    offsetFromBoundary,
    endPosition,
    overflowRight,
    overflowLeft,
  };
};

const calculateMenuOverflow = ({
  popperWidth,
  referencePosition,
  boundaryWidth,
  referenceWidth,
  isRTL,
}: CalculateMenuOverflowOptions) => {
  const overflowRight = isRTL
    ? boundaryWidth -
      (referencePosition.offsetFromBoundary +
        HORIZONTAL_PADDING_OFFSET +
        popperWidth)
    : referencePosition.overflowRight + HORIZONTAL_PADDING_OFFSET;

  const widthDiff = (referenceWidth - popperWidth) * -1;

  const overflowLeft = isRTL
    ? referencePosition.overflowLeft + HORIZONTAL_PADDING_OFFSET
    : referencePosition.offsetFromBoundary -
      widthDiff -
      HORIZONTAL_PADDING_OFFSET;

  return {
    left: overflowLeft,
    right: overflowRight,
  };
};

const calculateHorizontalOffset = ({
  rootElement,
  boundaryElement,
  referenceElement,
  popper: { width: popperWidth },
}: CalculateHorizontalOffsetOptions) => {
  const isRTL = getIsRTL(rootElement);
  const boundaryDimensions = getElementDimensions(boundaryElement);
  const referenceDimensions = getElementDimensions(referenceElement);

  const referencePosition = getReferencePosition({
    boundaryDimensions,
    referenceDimensions,
  });

  const menuOverflow = calculateMenuOverflow({
    popperWidth,
    referencePosition,
    boundaryWidth: boundaryDimensions.width,
    referenceWidth: referenceDimensions.width,
    isRTL,
  });

  if (menuOverflow.right < 0) {
    return menuOverflow.right;
  }

  return menuOverflow.left < 0 ? Math.abs(menuOverflow.left) : 0;
};

export const calculateOffset = ({
  popper,
  placement,
  rootElement,
  referenceElement,
  boundaryElement,
}: CalculateOffsetOptions) => {
  const horizontalOffset = calculateHorizontalOffset({
    rootElement,
    popper,
    boundaryElement,
    referenceElement,
  });

  const verticalOffset = calculateVerticalOffset(rootElement, placement);

  return [horizontalOffset, verticalOffset];
};
